/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useMemo } from 'react';

import type { Picture } from '@swf/cms';
import { ScreenSizeContext } from '@swf/core/providers/ScreenSizeProvider';
import type { SourceBlock } from '@swf/generated/cms/types/GlobalFields';

import CMSPreloadImage from '../CMSPreloadImage/CMSPreloadImage';

type BreakPoint = SourceBlock['minimum_screen_width'];

const sizes = new Map<BreakPoint, number>([
  ['xxs', 320],
  ['xs', 525],
  ['sm', 725],
  ['md', 930],
  ['lg', 1080],
  ['xl', 1230],
  ['xxl', 1440],
  ['xxxl', 1600]
]);
const sizeNames = Array.from(sizes.keys());

const findCurrentSource = (sources: SourceBlock[], currentSize: BreakPoint): SourceBlock => {
  const currentSource = sources.find((source) => source.minimum_screen_width === currentSize);
  if (!currentSource) {
    const sizeIndex = sizeNames.findIndex((size) => size === currentSize);
    if (sizeIndex > -1) {
      return findCurrentSource(sources, sizeNames[sizeIndex - 1]);
    } else {
      return sources[sources.length - 1];
    }
  }
  return currentSource;
};

const CMSPicture: React.FC<Picture> = ({ sources, alt }) => {
  const { screenSize } = useContext(ScreenSizeContext);
  const sortedSources = useMemo(
    () =>
      [...sources].sort(
        (source1, source2) =>
          sizeNames.findIndex((size) => size === source2.minimum_screen_width) -
          sizeNames.findIndex((size) => size === source1.minimum_screen_width)
      ),
    [sources]
  );
  const currentSource = findCurrentSource(sources, screenSize as BreakPoint);

  return (
    <>
      <CMSPreloadImage imageUrl={currentSource.asset.url} />
      <picture>
        {sortedSources.map(({ asset, minimum_screen_width }, index) => (
          <source
            srcSet={asset.url}
            media={
              minimum_screen_width ? `(min-width: ${sizes.get(minimum_screen_width)}px)` : undefined
            }
            key={index}
          />
        ))}
        <img src={currentSource.asset.url} alt={alt} />
      </picture>
    </>
  );
};
export default CMSPicture;
